import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useCredentialGapDialog } from '~/compositions/useCredentialGapDialog'
import { productTourIsRunning } from '~/compositions/useProductTour'
import { CREDENTIAL_PROMPTS_AVAILABLE } from '~/constants/features'
import { canBeAskedForCredentials as canBeAskedForCredentialsLogic } from '~/logic/credentialPromptEligibility'
import { useConnectionsStore } from '~/stores/connections'
import { useGlobalNotificationsStore } from '~/stores/globalNotifications'
import { useUserStore } from '~/stores/user'

/**
 * Decides which app-level modal wins when more than one wants the screen.
 *
 * `App.vue` and `layouts/default.vue` each mount modals that know nothing about
 * each other, and Element Plus hands out z-indexes from a global counter in the
 * order dialogs open (`use-dialog.mjs`: `zIndex = props.zIndex ?? nextZIndex()`).
 * Without an explicit priority the last one to open simply covers the others,
 * and `bringToFront` can't rescue it — that bails unless the dialog is
 * `penetrable`, which requires `:modal="false"`. So the ordering has to be
 * decided here rather than left to whichever API responds first.
 *
 * The order, strongest claim first:
 *
 * 1. a running product tour — not a modal at all, but the continuation of the
 *    one ranked below it. `useNotificationTourCta` acknowledges the popup away
 *    before starting the tour, deliberately, since the overlay has to highlight
 *    a page that is not behind a modal — so the tour inherits the claim the
 *    popup was making, and without a rung of its own the acknowledgement simply
 *    hands the screen to whatever was queued behind it
 * 2. the forced global notification popup — the only one with no escape, no
 *    overlay-click and no ✕, so anything covering it is a dead end
 * 3. the connection onboarding dialog
 * 4. the credential gap prompt — user-triggered (they just added containers or
 *    opened a booking), so it outranks the extension popup, but it is the
 *    onboarding dialog's smaller sibling and waits for it: a user mid-setup is
 *    about to be asked for these same credentials
 * 5. the browser-extension install popup — dismissable, and deferrable for 24h
 *
 * Everything here waits on `sessionReady` rather than `loggedIn`: the org
 * predicates these conditions are built from all read permissive defaults
 * while `userInfo` is still in flight, which is how a modal ends up on screen
 * for a user it was never meant for and then vanishes a second later.
 */
export function useAppModalPriority() {
  const route = useRoute()
  const userStore = useUserStore()
  const connectionsStore = useConnectionsStore()
  const notificationsStore = useGlobalNotificationsStore()
  const credentialGapDialog = useCredentialGapDialog()

  const forcedNotificationPopupIsShowing = computed(
    () => notificationsStore.unacknowledgedPopups.length > 0
  )

  const canBeAskedForCredentials = computed(() =>
    canBeAskedForCredentialsLogic(userStore)
  )

  /**
   * Whether the onboarding dialog has a claim on the screen at all, ignoring
   * both the route and anything ranked above it.
   *
   * This — not `shouldShowConnectionOnboarding` — is what the extension popup
   * yields to, for two reasons. The extension popup must keep yielding on the
   * pages where the dialog suppresses *itself* (settings, sign-up), the one
   * deliberate asymmetry between them. And while a forced popup is holding
   * both of them off, the extension popup must not slip in ahead of the
   * onboarding dialog that is merely queued behind it.
   */
  const connectionOnboardingWouldShow = computed(() => {
    // Internal users are exempt — the dialog is "super annoying" for them
    if (userStore.is_internal_user) return false
    // Nothing to offer a user who isn't allowed to edit connections anyway
    if (!canBeAskedForCredentials.value) return false
    if (!connectionsStore.shouldShowSetupModal) return false
    if (userStore.connectionSetupSkipped) return false
    return (
      connectionsStore.totalEditableConnectionTypes > 0 &&
      connectionsStore.remainingConnectionsCount > 0
    )
  })

  /** What the onboarding dialog itself renders on. */
  const shouldShowConnectionOnboarding = computed(() => {
    // Waits its turn behind the forced popup like everything else — otherwise
    // it opens over the one modal the user has no way to dismiss
    if (forcedNotificationPopupIsShowing.value) return false
    // ...and behind the tour that popup can launch, which is the same claim one
    // step later in the same flow. Ranked here rather than in
    // `connectionOnboardingWouldShow` for the reason that predicate documents:
    // it answers whether the dialog has a claim at all, and the extension popup
    // reads it to decide whether to yield. Suppressing it there would tell the
    // extension popup the coast was clear.
    if (productTourIsRunning.value) return false
    if (route.path.includes('settings')) return false
    // `loggedIn` fires before the redirect out of signup completes, so the
    // dialog would otherwise appear mid-signup (DEV-6133)
    if (route.path.includes('sign_up')) return false
    return connectionOnboardingWouldShow.value
  })

  /**
   * Whether a credential gap prompt has been asked for and this user can see
   * one. A pending request counts even while a higher modal holds it back, for
   * the same reason `connectionOnboardingWouldShow` does: the extension popup must
   * not slip in ahead of a prompt that is merely queued.
   */
  const credentialGapPromptWouldShow = computed(
    () =>
      CREDENTIAL_PROMPTS_AVAILABLE &&
      canBeAskedForCredentials.value &&
      credentialGapDialog.current.value !== null
  )

  /** What the credential gap prompt itself renders on. */
  const shouldShowCredentialGapPrompt = computed(
    () =>
      credentialGapPromptWouldShow.value &&
      !forcedNotificationPopupIsShowing.value &&
      !productTourIsRunning.value &&
      !connectionOnboardingWouldShow.value
  )

  /**
   * Whether the browser-extension install popup may take the screen. Gated on
   * `sessionReady` in its own right: allowing it through the hydration window
   * only to retract it once a higher-priority modal resolves is the flicker
   * this whole chain exists to prevent.
   */
  const extensionPopupIsAllowed = computed(
    () =>
      userStore.sessionReady &&
      !forcedNotificationPopupIsShowing.value &&
      !productTourIsRunning.value &&
      !connectionOnboardingWouldShow.value &&
      !credentialGapPromptWouldShow.value
  )

  return {
    canBeAskedForCredentials,
    connectionOnboardingWouldShow,
    shouldShowConnectionOnboarding,
    shouldShowCredentialGapPrompt,
    extensionPopupIsAllowed,
  }
}
